import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  Home,
  Settings,
  Users,
  Calendar,
  MessageSquare,
  User,
  FileText,
  CreditCard,
  LayoutDashboard,
  Car,
  Clock,
  History,
  X,
  RefreshCw,
  Book,
  Database,
  MapPin,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface DashboardSidebarProps {
  role: "admin" | "student" | "instructor";
  isMobile?: boolean;
  onClose?: () => void;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ role, isMobile = false, onClose }) => {
  const location = useLocation();
  const pathName = location.pathname;

  const adminNavItems = [
    {
      title: "Dashboard",
      href: "/admin-dashboard",
      icon: <LayoutDashboard className="mr-2 h-5 w-5" />,
    },
    {
      title: "Users",
      href: "/admin-dashboard/users",
      icon: <Users className="mr-2 h-5 w-5" />,
    },
    {
      title: "Courses",
      href: "/admin-dashboard/courses",
      icon: <BookOpen className="mr-2 h-5 w-5" />,
    },
    {
      title: "Instructors",
      href: "/admin-dashboard/instructors",
      icon: <Car className="mr-2 h-5 w-5" />,
    },
    {
      title: "Bookings",
      href: "/admin-dashboard/bookings",
      icon: <Calendar className="mr-2 h-5 w-5" />,
    },
    {
      title: "Book on Behalf",
      href: "/admin-dashboard/book-on-behalf",
      icon: <Calendar className="mr-2 h-5 w-5" />,
    },
    {
      title: "Areas Covered",
      href: "/admin-dashboard/areas",
      icon: <MapPin className="mr-2 h-5 w-5" />,
    },
    {
      title: "Content",
      href: "/admin-dashboard/content",
      icon: <BookOpen className="mr-2 h-5 w-5" />,
    },
    {
      title: "Finance",
      href: "/admin-dashboard/finance",
      icon: <CreditCard className="mr-2 h-5 w-5" />,
    },
    {
      title: "Student Payments",
      href: "/admin-dashboard/student-payments",
      icon: <CreditCard className="mr-2 h-5 w-5" />,
    },
    {
      title: "Update Student Data",
      href: "/admin-dashboard/update-student-data",
      icon: <RefreshCw className="mr-2 h-5 w-5" />,
    },
    {
      title: "System Backup",
      href: "/admin-dashboard/system-backup",
      icon: <Database className="mr-2 h-5 w-5" />,
    },
    {
      title: "Settings",
      href: "/admin-dashboard/settings",
      icon: <Settings className="mr-2 h-5 w-5" />,
    },
  ];

  const studentLinks = [
    {
      title: "Dashboard",
      href: "/student-dashboard",
      icon: <Home className="mr-2 h-5 w-5" />,
    },
    {
      title: "Buy Driving Lessons",
      href: "/student-dashboard/courses",
      icon: <BookOpen className="mr-2 h-5 w-5" />,
    },
    {
      title: "My Paid Driving Lessons",
      href: "/student-dashboard/my-enrollments",
      icon: <BookOpen className="mr-2 h-5 w-5" />,
    },
    {
      title: "My Schedules",
      href: "/student-dashboard/schedules",
      icon: <Calendar className="mr-2 h-5 w-5" />,
    },
    {
      title: "My Lessons",
      href: "/student-dashboard/lesson-history",
      icon: <History className="mr-2 h-5 w-5" />,
    },
    {
      title: "Payments",
      href: "/student-dashboard/payments",
      icon: <CreditCard className="mr-2 h-5 w-5" />,
    },
    {
      title: "Student Stories",
      href: "/student-dashboard/stories",
      icon: <MessageSquare className="mr-2 h-5 w-5" />,
    },
    {
      title: "Chat",
      href: "/chat",
      icon: <MessageSquare className="mr-2 h-5 w-5" />,
    },
    {
      title: "Profile",
      href: "/student-dashboard/profile",
      icon: <User className="mr-2 h-5 w-5" />,
    },
    {
      title: "Settings",
      href: "/student-dashboard/settings",
      icon: <Settings className="mr-2 h-5 w-5" />,
    },
  ];

  const instructorLinks = [
    {
      title: "Dashboard",
      href: "/instructor-dashboard",
      icon: <Home className="mr-2 h-5 w-5" />,
    },
    {
      title: "Enrolled Students",
      href: "/instructor-dashboard/enrolled-students",
      icon: <Users className="mr-2 h-5 w-5" />,
    },
    {
      title: "Student Schedules",
      href: "/instructor-dashboard/schedules",
      icon: <Calendar className="mr-2 h-5 w-5" />,
    },
    {
      title: "Book Lesson",
      href: "/instructor/book-lesson",
      icon: <Book className="mr-2 h-5 w-5" />,
    },
    {
      title: "Student Payments",
      href: "/instructor-dashboard/student-payments",
      icon: <CreditCard className="mr-2 h-5 w-5" />,
    },
    {
      title: "Mock Test Results",
      href: "/instructor-dashboard/mock-test-results",
      icon: <FileText className="mr-2 h-5 w-5" />,
    },
    {
      title: "Chat",
      href: "/chat",
      icon: <MessageSquare className="mr-2 h-5 w-5" />,
    },
    {
      title: "Availability",
      href: "/instructor-dashboard/availability",
      icon: <Clock className="mr-2 h-5 w-5" />,
    },
    {
      title: "Profile",
      href: "/instructor-dashboard/profile",
      icon: <User className="mr-2 h-5 w-5" />,
    },
    {
      title: "Settings",
      href: "/instructor-dashboard/settings",
      icon: <Settings className="mr-2 h-5 w-5" />,
    },
  ];

  const links = {
    admin: adminNavItems,
    student: studentLinks,
    instructor: instructorLinks,
  };

  return (
    <div className={cn(
      "bg-gradient-to-b from-gray-50 to-white border-r border-gray-200 min-h-screen p-4",
      isMobile ? "w-full" : "w-64 hidden md:block"
    )}>
      <div className="space-y-4">
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="px-4 text-lg font-semibold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-drivewise-600 to-blue-600">
              {role.charAt(0).toUpperCase() + role.slice(1)} Dashboard
            </h2>
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="md:hidden"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
          <div className="space-y-1">
            {links[role].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={isMobile ? onClose : undefined}
                className={cn(
                  "flex items-center justify-start w-full rounded-lg px-3 py-2 text-sm transition-colors",
                  pathName === link.href
                    ? "bg-gradient-to-r from-drivewise-100 to-blue-100 text-drivewise-900 font-medium"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                )}
              >
                {link.icon}
                {link.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSidebar;
